import { get, patch, post, put, del } from './httpClient';
import {
  putUserLotAttachmentToS3,
  normalizeUserLotAttachmentFileName,
} from './userLotsService';
import { CAD_PARCEL_EDITABLE_KEYS } from '../Showroom/data/cadConstants';
import { pickCadShareExtras, readCadInvite } from '../Showroom/utils/cadAccess';
import { normalizeCadParcelCommercial, normalizeCadProjectCommercial } from '../Leads/commercialSummary';

function isAbortError(error) {
  return error?.name === 'AbortError';
}

function pickEditableFields(body) {
  const next = {};
  CAD_PARCEL_EDITABLE_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(body, key)) {
      next[key] = body[key];
    }
  });
  return next;
}

function readCadApiError(response, fallback) {
  const text = response?.error || response?.message;
  const trimmed = typeof text === 'string' ? text.trim() : '';
  return trimmed || fallback;
}

function withStatus(payload, response) {
  return {
    ...payload,
    _httpStatus: response?._httpStatus ?? null,
  };
}

/**
 * GET /getcadproject?id=<uuid>. El slug puede repetirse; `?slug=` solo → 400.
 * `public` + shared → 200 sin JWT. `restricted` → JWT + allowlist (401/403).
 * Sin share y sin derecho → 404. Con sesión, el JWT rellena `can_manage` /
 * `can_publish` / `can_edit`. Manage (`can_manage`): siempre trae `ref_lot_id`
 * (`lots.id` o `null`) y `georef_src_corners`. Editor (`can_edit`) también
 * recibe `canvas_image` (pintar; Referenciar sigue siendo del dueño).
 * `show_plan_image`: si el PNG se pinta o se deja el loteo (calles/plaza).
 * Tener archivo no implica mostrarlo. Geometría = la del `display_mode`.
 * Incluye `share_template` + `owner_branding` (marca del Owner).
 * `parcel_count` = solo `cad_kind: lot` (o sin kind); `parcels.features`
 * sigue trayendo calle / verde / predio.
 * `pins`: pines custom del dueño (`[]` si no hay). No es `reference_places`.
 *
 *
 * @param {string|{ id?: string }} idOrOpts
 *   string = UUID del proyecto; objeto = `{ id }` (otras claves se ignoran).
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 *   `ownerUserId` → `?owner_user_id=` (F3-CAD, diálogo de share del invitado en
 *   espacio ajeno: trae `allowlist` + `can_share`). Sin él → request de siempre.
 * `includeCommercialSummary` → `include=commercial_summary` en
 *   `parcels.features[].properties` (solo manage; dueño / editor, nunca share).
 * `viewerCanManage` (SQL 099): `viewer_can_manage` de la respuesta (form de consulta en modo cliente).
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', project: object|null, viewerCanManage?: boolean, message?: string, _httpStatus: number|null }>}
 */
export async function getCadProject(idOrOpts, { signal, ownerUserId, includeCommercialSummary = false } = {}) {
  let id = '';
  if (typeof idOrOpts === 'string') {
    id = idOrOpts.trim();
  } else if (idOrOpts && typeof idOrOpts === 'object') {
    id = String(idOrOpts.id || '').trim();
  }

  if (!id) {
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'Falta el id del proyecto.',
      _httpStatus: null,
    };
  }

  try {
    const owner_user_id = ownerUserId ? String(ownerUserId).trim() : '';
    const response = await get('/getcadproject', {
      params: {
        id,
        ...(owner_user_id ? { owner_user_id } : {}),
        // Solo con `viewer_can_manage`; si no, la API lo ignora (sin clave).
        ...(includeCommercialSummary ? { include: 'commercial_summary' } : {}),
      },
      signal,
    });
    if (response._httpOk && response.data) {
      return withStatus({
        ok: true,
        source: 'api',
        project: includeCommercialSummary ? normalizeCadProjectCommercial(response.data) : response.data,
        // SQL 099: raíz; tolera dentro de `data`.
        viewerCanManage: response.viewer_can_manage === true || response.data?.viewer_can_manage === true,
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      project: null,
      message: readCadApiError(response, 'No se pudo cargar la lista.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: GET /getcadproject falló', error);
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

function cadGuestShareBody({ id, projectId } = {}) {
  const trimmedId = String(id || projectId || '').trim();
  if (!trimmedId) return {};
  return { project_id: trimmedId };
}

async function postCadGuestShareAction(path, { id, projectId } = {}, { signal } = {}, fallback) {
  const body = cadGuestShareBody({ id, projectId });
  if (!body.project_id) {
    return {
      ok: false,
      source: 'none',
      message: 'Falta el id de la lista.',
      _httpStatus: null,
    };
  }
  try {
    const response = await post(path, { body, signal });
    if (response._httpOk) {
      return withStatus({ ok: true, source: 'api' }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      message: readCadApiError(response, fallback),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error(`CAD showroom: POST ${path} falló`, error);
    return {
      ok: false,
      source: 'none',
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * GET /getcadprojects. JWT. Sin query = listas propias (owner del JWT).
 * Editor de empresa: viene con `can_manage: true`, `can_publish: false` →
 * Mis listas. Con `?owner_user_id=` ajeno (espejo de espacio): miembro del
 * espacio recibe las listas del owner con `can_manage`/`can_publish` false;
 * no miembro → 403. Invitados restricted por colección **nunca** acá: van a
 * `GET /getsharedwithmecadprojects`.
 * Aditivo: `lat`/`lng` (centroide WGS84 si `display_mode=map`; envelope de
 * todas las geometrías), `parcel_count` (solo lotes: `cad_kind: lot` o
 * sin kind; no street/green/site),
 * `brand: { id, brand_name, primary_color }` si el share es personalized.
 *
 * @param {{ signal?: AbortSignal, ownerUserId?: string }} [options]
 *        `ownerUserId` → `?owner_user_id=` (espejo de espacio; ver espacios-invite).
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', projects: object[], message?: string, _httpStatus: number|null }>}
 * @see docs/maps/espacios-invite.md
 */
export async function getCadProjects({ signal, ownerUserId } = {}) {
  try {
    const owner_user_id = ownerUserId ? String(ownerUserId).trim() : '';
    const response = await get('/getcadprojects', {
      signal,
      params: owner_user_id ? { owner_user_id } : undefined,
    });
    if (response._httpOk) {
      const projects = Array.isArray(response.data) ? response.data : [];
      return withStatus({ ok: true, source: 'api', projects }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      projects: [],
      message: readCadApiError(response, 'No se pudieron cargar las listas.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: GET /getcadprojects falló', error);
    return {
      ok: false,
      source: 'none',
      projects: [],
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * GET /getsharedwithmecadprojects. JWT. Showrooms restricted compartidos
 * con la sesión (allowlist) o donde sos editor (`can_edit: true`).
 * Un share public no aparece: el invitado entra por `/#/lista/:id`.
 * `parcel_count` = solo lotes, igual que `/getcadprojects`.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', projects: object[], message?: string, _httpStatus: number|null }>}
 */
export async function getSharedWithMeCadProjects({ signal } = {}) {
  try {
    const response = await get('/getsharedwithmecadprojects', { signal });
    if (response._httpOk) {
      const projects = Array.isArray(response.data) ? response.data : [];
      return withStatus({ ok: true, source: 'api', projects }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      projects: [],
      message: readCadApiError(response, 'No se pudieron cargar las listas compartidas.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: GET /getsharedwithmecadprojects falló', error);
    return {
      ok: false,
      source: 'none',
      projects: [],
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * POST /leavesharedcadproject. Saca la allowlist del invitado y lo saca
 * de Compartidas conmigo. El panel usa este path (no hide).
 * Body: `{ id }` o `{ project_id }` (el slug ya no identifica).
 * @param {{ id?: string, projectId?: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 */
export function leaveSharedCadProject(body, options) {
  return postCadGuestShareAction(
    '/leavesharedcadproject',
    body,
    options,
    'No se pudo dejar de ver la lista.',
  );
}

/**
 * GET /getmycadparcels. JWT. Parcelas planas de showrooms que podés
 * gestionar (owner o editor de la empresa). Un `id` de parcela no es
 * un `lot_id`. Invitados restricted no entran (siguen en Compartidas).
 * Sin `include`: sin clave `geometry` (Registros).
 * `include: 'geometry'`: misma fila + GeoJSON 4326 o `null` (mapa país).
 * Si da 500 en DEV, falta la ruta en API Gateway.
 *
 * @param {{ include?: string, signal?: AbortSignal, ownerUserId?: string }} [options]
 *        `ownerUserId` → `?owner_user_id=` (espejo de espacio; ver espacios-invite).
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   parcels: object[],
 *   total: number,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 * @see docs/maps/registros-cad-parcels.md
 * @see docs/maps/cad-owner-map-overlay-backend-spec.md
 * @see docs/maps/espacios-invite.md
 */
export async function getMyCadParcels({ include, signal, ownerUserId } = {}) {
  // `include`: 'geometry', 'commercial_summary' o ambos separados por coma
  // (commercial_summary suma ~20% de latencia; solo Registros).
  const includeList = String(include || '').split(',').map((v) => v.trim())
    .filter((v) => v === 'geometry' || v === 'commercial_summary');
  const includeParam = includeList.join(',');
  try {
    const owner_user_id = ownerUserId ? String(ownerUserId).trim() : '';
    const params = {
      ...(includeParam ? { include: includeParam } : {}),
      ...(owner_user_id ? { owner_user_id } : {}),
    };
    const response = await get('/getmycadparcels', {
      signal,
      params: Object.keys(params).length ? params : undefined,
    });
    if (response._httpOk) {
      const rows = Array.isArray(response.data) ? response.data : [];
      const parcels = includeList.includes('commercial_summary')
        ? rows.map(normalizeCadParcelCommercial)
        : rows;
      const total = typeof response.total === 'number' ? response.total : parcels.length;
      return withStatus({ ok: true, source: 'api', parcels, total }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      parcels: [],
      total: 0,
      message: readCadApiError(response, 'No se pudieron cargar los lotes.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD registros: GET /getmycadparcels falló', error);
    return {
      ok: false,
      source: 'none',
      parcels: [],
      total: 0,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * DELETE /deletecadparcel?id=. JWT. Staff u owner/editor (`can_manage`).
 * Igual que sacar un lote de una lista: la fila se borra (no es catastro).
 * 200 `{ id, project_slug }`. Sin derecho o id inexistente → 404.
 *
 * @param {{ id: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function deleteCadParcel({ id } = {}, { signal } = {}) {
  const trimmed = String(id || '').trim();
  if (!trimmed) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el id del lote.',
      _httpStatus: null,
    };
  }
  try {
    const response = await del('/deletecadparcel', {
      params: { id: trimmed },
      signal,
    });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        data: response.data || { id: trimmed },
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, 'No se pudo eliminar el lote.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: DELETE /deletecadparcel falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * DELETE /deletecadproject?id=<uuid>. Sin id → no se llama (`?slug=` solo → 400).
 * JWT. Staff u Owner (`can_publish`). Editor → 403. Ajeno → 404.
 * CASCADE de parcelas, allowlist y ocultos. 200 `{ id?, slug, name, parcels_deleted }`.
 *
 * @param {{ id?: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function deleteCadProject({ id } = {}, { signal } = {}) {
  const trimmedId = String(id || '').trim();
  if (!trimmedId) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el id del proyecto.',
      _httpStatus: null,
    };
  }
  try {
    const response = await del('/deletecadproject', {
      params: { id: trimmedId },
      signal,
    });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        data: response.data || { id: trimmedId },
      }, response);
    }
    const isForbidden = response._httpStatus === 403;
    const fallback = isForbidden
      ? 'No podés eliminar esta lista.'
      : 'No se pudo eliminar la lista.';
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, fallback),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: DELETE /deletecadproject falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * PATCH /updatecadparcel. JWT obligatorio. Staff u owner/editor de la empresa.
 * Sin derecho o id inexistente → 404. No edita geometría ni fotos
 * (`photos` van por los endpoints de imagen). `area_m2` es opcional:
 * número > 0 (tope 1.000.000). No mandar `null` ni `geom`.
 * `cad_kind` (`lot` | `street` | `green` | `site`): Owner/staff (`can_manage`).
 * Editor de colección → 400 (igual que `use` / `fos`). No cambia el polígono
 * ni `name`. El GET sigue en `properties.attributes.cad_kind`. Si pasa a
 * street / green / site y no mandás `use` ni `stock_status`, el API pone
 * amenity + unavailable.
 *
 * @param {{ id: string } & Record<string, unknown>} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function updateCadParcel(body, { signal } = {}) {
  const id = body?.id;
  const patchBody = { id, ...pickEditableFields(body || {}) };

  try {
    const response = await patch('/updatecadparcel', {
      body: patchBody,
      signal,
    });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        data: response.data || patchBody,
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, 'No se pudo guardar el lote.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: PATCH /updatecadparcel falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

function readCadImagePayload(response) {
  if (response?.data != null && typeof response.data === 'object') return response.data;
  return response;
}

/**
 * GET /getcadparcelpresignedurl?parcel_id=&filename=&contentType=.
 * JWT + can_manage. El PUT a S3 tiene que usar el mismo Content-Type.
 *
 * @param {{ parcelId: string, filename: string, contentType: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function getCadParcelPresignedUrl(
  { parcelId, filename, contentType } = {},
  { signal } = {},
) {
  const trimmedId = String(parcelId || '').trim();
  if (!trimmedId || !contentType) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el lote o el tipo de archivo.',
      _httpStatus: null,
    };
  }
  try {
    const response = await get('/getcadparcelpresignedurl', {
      params: {
        parcel_id: trimmedId,
        filename: filename || undefined,
        contentType,
      },
      signal,
    });
    const data = readCadImagePayload(response);
    if (response._httpOk && data?.presignedUrl) {
      return withStatus({ ok: true, source: 'api', data }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, 'No se obtuvo la URL de subida.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: GET /getcadparcelpresignedurl falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * PUT del archivo a la URL prefirmada. Mismo Content-Type con el que se firmó.
 *
 * @param {{ presignedUrl: string, file: File|Blob, contentType: string }} params
 * @returns {Promise<{ ok: boolean }>}
 */
export async function putCadParcelImageToS3({ presignedUrl, file, contentType } = {}) {
  if (!presignedUrl || !file || !contentType) return { ok: false };
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  return { ok: response.ok };
}

/**
 * GET /savecadparcelimage?parcel_id=&image_url=. Persiste la fila.
 * Primera foto = portada. JWT + can_manage.
 *
 * @param {{ parcelId: string, imageUrl: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function saveCadParcelImage({ parcelId, imageUrl } = {}, { signal } = {}) {
  const trimmedId = String(parcelId || '').trim();
  const url = String(imageUrl || '').trim();
  if (!trimmedId || !url) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el lote o la URL de la foto.',
      _httpStatus: null,
    };
  }
  try {
    const response = await get('/savecadparcelimage', {
      params: { parcel_id: trimmedId, image_url: url },
      signal,
    });
    const data = readCadImagePayload(response);
    if (response._httpOk && data) {
      return withStatus({ ok: true, source: 'api', data }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, 'No se pudo guardar la foto.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: GET /savecadparcelimage falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * DELETE /deletecadparcelimage?image_id=. Última foto → 200 + cover_image null.
 * JWT + can_manage.
 *
 * @param {{ imageId: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function deleteCadParcelImage({ imageId } = {}, { signal } = {}) {
  const trimmedId = String(imageId || '').trim();
  if (!trimmedId) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el id de la foto.',
      _httpStatus: null,
    };
  }
  try {
    const response = await del('/deletecadparcelimage', {
      params: { image_id: trimmedId },
      signal,
    });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        data: readCadImagePayload(response) || {},
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, 'No se pudo eliminar la foto.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: DELETE /deletecadparcelimage falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * PUT /setcadparcelimagecover?parcel_id=&image_id=. JWT + can_manage.
 *
 * @param {{ parcelId: string, imageId: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function setCadParcelCoverImage({ parcelId, imageId } = {}, { signal } = {}) {
  const trimmedParcel = String(parcelId || '').trim();
  const trimmedImage = String(imageId || '').trim();
  if (!trimmedParcel || !trimmedImage) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el lote o la foto.',
      _httpStatus: null,
    };
  }
  try {
    const response = await put('/setcadparcelimagecover', {
      params: { parcel_id: trimmedParcel, image_id: trimmedImage },
      signal,
    });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        data: readCadImagePayload(response) || {},
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, 'No se pudo cambiar la portada.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: PUT /setcadparcelimagecover falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * PATCH /updatecadproject. JWT. Staff u Owner. Editor → 403. Ajeno → 404.
 * Mismo share que listas: `is_shared` + `access_mode`. No mandar `visibility`.
 * `share_template` + invite SES: mismas claves que listas. PATCH parcial:
 * si omitís `share_template` se conserva. `brand_id` elige con qué identidad
 * se pinta; omitirlo conserva la que estaba, y `null` vuelve a "la principal"
 * (403 si la marca es de otra cuenta).
 * `inquiry_email_source` se ignora al enviar la consulta (compat): el To es
 * el mail de la identidad del share, o el de la cuenta del dueño.
 * Georef (Owner, `can_publish`): `georef_points` (3 pares del sticker). `src` =
 * metros fijos (`georef_src_corners`); `lnglat` = mapa. Si hay,
 * `ref_lot_id` (`lots.id`). Sin ID el API conserva o infiere el lote del
 * centro. Atajo solo lote: bbox sin rotar. Escribe `geom_4326` y pasa a `map`.
 * `ref_lot_id: null` quita la referencia
 * (borra WGS84 y puntos; el plano en metros no se toca; vuelve a canvas).
 * `{ display_mode: "canvas" }` sin null solo cambia el lienzo.
 * Owner/Editor → 403.
 *
 * @param {{
 *   id: string,
 *   is_shared?: boolean,
 *   access_mode?: 'public'|'restricted',
 *   allowlist?: object[],
 *   show_cadastral_base?: boolean,
 *   show_plan_image?: boolean,
 *   display_mode?: 'map'|'canvas'|'image',
 *   share_template?: 'default'|'personalized',
 *   brand_id?: string|null,
 *   inquiry_email_source?: 'brand'|'company'|'owner'|null,
 *   send_invite?: boolean,
 *   invite_message?: string,
 *   invite_emails?: string[],
 *   name?: string,
 *   ref_lot_id?: string|null,
 *   georef_points?: { src: number[], lnglat: number[] }[],
 *   canvas_image_transform?: {
 *     dx?: number,
 *     dy?: number,
 *     scale?: number,
 *     scaleX?: number,
 *     scaleY?: number,
 *     rotationRad?: number,
 *   },
 *   ownerUserId?: string|null,
 * }} body
 *   `ownerUserId` → `owner_user_id` en el body (F3-CAD: el invitado comparte
 *   el showroom del owner; la API solo acepta claves de share). Sin él → body
 *   de siempre.
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', project: object|null, invite?: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function updateCadProject(body, { signal } = {}) {
  const id = String(body?.id || '').trim();
  const patchBody = {};
  if (id) patchBody.id = id;
  if (typeof body?.is_shared === 'boolean') {
    patchBody.is_shared = body.is_shared;
  }
  if (typeof body?.access_mode === 'string' && body.access_mode.trim()) {
    patchBody.access_mode = body.access_mode.trim();
  }
  if (Array.isArray(body?.allowlist)) {
    patchBody.allowlist = body.allowlist;
  }
  if (typeof body?.show_cadastral_base === 'boolean') {
    patchBody.show_cadastral_base = body.show_cadastral_base;
  }
  if (typeof body?.show_plan_image === 'boolean') {
    patchBody.show_plan_image = body.show_plan_image;
  }
  if (typeof body?.display_mode === 'string' && body.display_mode.trim()) {
    patchBody.display_mode = body.display_mode.trim();
  }
  if (typeof body?.name === 'string') {
    patchBody.name = body.name;
  }
  if (Object.prototype.hasOwnProperty.call(body || {}, 'ref_lot_id')) {
    if (body.ref_lot_id == null || body.ref_lot_id === '') {
      patchBody.ref_lot_id = null;
    } else if (typeof body.ref_lot_id === 'string' && body.ref_lot_id.trim()) {
      patchBody.ref_lot_id = body.ref_lot_id.trim();
    }
  }
  if (Array.isArray(body?.georef_points)) {
    patchBody.georef_points = body.georef_points;
  }
  // No filtrar esta clave: sin ella el GET no trae el encaje y el visitante
  // ve contain. Los seis números (Ancho/Alto = scaleX/scaleY) van enteros.
  if (
    body?.canvas_image_transform
    && typeof body.canvas_image_transform === 'object'
    && !Array.isArray(body.canvas_image_transform)
  ) {
    patchBody.canvas_image_transform = body.canvas_image_transform;
  }
  Object.assign(patchBody, pickCadShareExtras(body));
  const ownerUserId = body?.ownerUserId ? String(body.ownerUserId).trim() : '';
  if (ownerUserId) patchBody.owner_user_id = ownerUserId;

  const hasUpdate = Object.prototype.hasOwnProperty.call(patchBody, 'is_shared')
    || Object.prototype.hasOwnProperty.call(patchBody, 'access_mode')
    || Object.prototype.hasOwnProperty.call(patchBody, 'allowlist')
    || Object.prototype.hasOwnProperty.call(patchBody, 'show_cadastral_base')
    || Object.prototype.hasOwnProperty.call(patchBody, 'show_plan_image')
    || Object.prototype.hasOwnProperty.call(patchBody, 'display_mode')
    || Object.prototype.hasOwnProperty.call(patchBody, 'share_template')
    || Object.prototype.hasOwnProperty.call(patchBody, 'brand_id')
    || Object.prototype.hasOwnProperty.call(patchBody, 'inquiry_email_source')
    || Object.prototype.hasOwnProperty.call(patchBody, 'send_invite')
    || Object.prototype.hasOwnProperty.call(patchBody, 'name')
    || Object.prototype.hasOwnProperty.call(patchBody, 'ref_lot_id')
    || Object.prototype.hasOwnProperty.call(patchBody, 'georef_points')
    || Object.prototype.hasOwnProperty.call(patchBody, 'canvas_image_transform');

  if (!id || !hasUpdate) {
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'Falta el id o el campo a actualizar.',
      _httpStatus: null,
    };
  }

  try {
    const response = await patch('/updatecadproject', {
      body: patchBody,
      signal,
    });
    if (response._httpOk) {
      const project = response.data || patchBody;
      return withStatus({
        ok: true,
        source: 'api',
        project,
        invite: readCadInvite(response) || readCadInvite(project),
      }, response);
    }
    const isForbidden = response._httpStatus === 403;
    const fallback = isForbidden
      ? 'No podés actualizar esta lista.'
      : 'No se pudo actualizar la lista.';
    return withStatus({
      ok: false,
      source: 'api',
      project: null,
      message: readCadApiError(response, fallback),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: PATCH /updatecadproject falló', error);
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * GET /getcadcanvaspresignedurl?id=&filename=&contentType=.
 * JWT + Owner/staff (`can_publish`). Editor → 403. El PUT a S3 usa el mismo
 * Content-Type (png / jpeg / webp).
 *
 * @param {{ id: string, filename: string, contentType: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function getCadCanvasPresignedUrl(
  { id, filename, contentType } = {},
  { signal } = {},
) {
  const trimmedId = String(id || '').trim();
  if (!trimmedId || !contentType) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el id de la lista o el tipo de archivo.',
      _httpStatus: null,
    };
  }
  try {
    const response = await get('/getcadcanvaspresignedurl', {
      params: {
        id: trimmedId,
        filename: filename || undefined,
        contentType,
      },
      signal,
    });
    const data = readCadImagePayload(response);
    if (response._httpOk && data?.presignedUrl) {
      return withStatus({ ok: true, source: 'api', data }, response);
    }
    const isForbidden = response._httpStatus === 403;
    const fallback = isForbidden
      ? 'No podés cambiar la ilustración de esta lista.'
      : 'No se obtuvo la URL de subida.';
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, fallback),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: GET /getcadcanvaspresignedurl falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/** PUT del render a la URL prefirmada. Mismo Content-Type con el que se firmó. */
export async function putCadCanvasImageToS3(params) {
  return putCadParcelImageToS3(params);
}

/**
 * POST /savecadcanvasimage `{ id, s3Key }`. También acepta `image_url` / `s3Url`
 * del presign. JWT + Owner/staff. Editor → 403. Si el lienzo era `canvas`,
 * pasa a `image`. Si era `map`, sigue `map`.
 *
 * @param {{ id: string, s3Key?: string, imageUrl?: string, s3Url?: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', project: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function saveCadCanvasImage(
  { id, s3Key, imageUrl, s3Url } = {},
  { signal } = {},
) {
  const trimmedId = String(id || '').trim();
  const key = String(s3Key || '').trim();
  const url = String(s3Url || imageUrl || '').trim();
  if (!trimmedId || (!key && !url)) {
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'Falta el id de la lista o la key de la ilustración.',
      _httpStatus: null,
    };
  }
  const body = { id: trimmedId };
  if (key) body.s3Key = key;
  if (url) {
    body.s3Url = url;
    body.image_url = url;
  }
  try {
    const response = await post('/savecadcanvasimage', { body, signal });
    const data = readCadImagePayload(response);
    if (response._httpOk && data) {
      return withStatus({ ok: true, source: 'api', project: data.data || data }, response);
    }
    const isForbidden = response._httpStatus === 403;
    const fallback = isForbidden
      ? 'No podés cambiar la ilustración de esta lista.'
      : 'No se pudo guardar la ilustración.';
    return withStatus({
      ok: false,
      source: 'api',
      project: null,
      message: readCadApiError(response, fallback),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: POST /savecadcanvasimage falló', error);
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * DELETE /deletecadcanvasimage?id=. `canvas_image: null`. Si el lienzo era
 * `image`, vuelve a `canvas`. El georef no se toca. JWT + Owner/staff.
 *
 * @param {{ id: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', project: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function deleteCadCanvasImage({ id } = {}, { signal } = {}) {
  const trimmedId = String(id || '').trim();
  if (!trimmedId) {
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'Falta el id de la lista.',
      _httpStatus: null,
    };
  }
  try {
    const response = await del('/deletecadcanvasimage', {
      params: { id: trimmedId },
      signal,
    });
    const data = readCadImagePayload(response);
    if (response._httpOk) {
      return withStatus({ ok: true, source: 'api', project: data?.data || data || null }, response);
    }
    const isForbidden = response._httpStatus === 403;
    const fallback = isForbidden
      ? 'No podés cambiar la ilustración de esta lista.'
      : 'No se pudo quitar la ilustración.';
    return withStatus({
      ok: false,
      source: 'api',
      project: null,
      message: readCadApiError(response, fallback),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: DELETE /deletecadcanvasimage falló', error);
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * POST /createcadprojectpin. JWT + owner/staff/editor de esa colección.
 * Ajeno → 404. Tope 50 pines. Body: project_id, name, kind?, notes?,
 * lat/lng y/o local_x/local_y.
 *
 * @param {object} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', pin: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function createCadProjectPin(body, { signal } = {}) {
  const projectId = String(body?.project_id || '').trim();
  const name = String(body?.name || '').trim();
  if (!projectId || !name) {
    return {
      ok: false,
      source: 'none',
      pin: null,
      message: 'Falta el proyecto o el nombre del pin.',
      _httpStatus: null,
    };
  }
  const payload = { project_id: projectId, name };
  if (typeof body?.kind === 'string' && body.kind.trim()) {
    payload.kind = body.kind.trim().slice(0, 40);
  }
  if (body?.notes != null) {
    const notes = String(body.notes).trim();
    payload.notes = notes || null;
  }
  if (Number.isFinite(Number(body?.lat)) && Number.isFinite(Number(body?.lng))) {
    payload.lat = Number(body.lat);
    payload.lng = Number(body.lng);
  }
  if (Number.isFinite(Number(body?.local_x)) && Number.isFinite(Number(body?.local_y))) {
    payload.local_x = Number(body.local_x);
    payload.local_y = Number(body.local_y);
  }
  if (
    !Object.prototype.hasOwnProperty.call(payload, 'lat')
    && !Object.prototype.hasOwnProperty.call(payload, 'local_x')
  ) {
    return {
      ok: false,
      source: 'none',
      pin: null,
      message: 'Faltan las coordenadas del pin.',
      _httpStatus: null,
    };
  }
  try {
    const response = await post('/createcadprojectpin', { body: payload, signal });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        pin: response.data || payload,
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      pin: null,
      message: readCadApiError(response, 'No se pudo crear el pin.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: POST /createcadprojectpin falló', error);
    return {
      ok: false,
      source: 'none',
      pin: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * PATCH /updatecadprojectpin. Campos opcionales; hay que mandar al menos uno.
 *
 * @param {object} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', pin: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function updateCadProjectPin(body, { signal } = {}) {
  const id = String(body?.id || '').trim();
  if (!id) {
    return {
      ok: false,
      source: 'none',
      pin: null,
      message: 'Falta el id del pin.',
      _httpStatus: null,
    };
  }
  const payload = { id };
  if (typeof body?.name === 'string') payload.name = body.name.trim();
  if (Object.prototype.hasOwnProperty.call(body || {}, 'kind')) {
    const kind = body.kind == null ? null : String(body.kind).trim().slice(0, 40);
    payload.kind = kind || null;
  }
  if (Object.prototype.hasOwnProperty.call(body || {}, 'notes')) {
    const notes = body.notes == null ? null : String(body.notes).trim();
    payload.notes = notes || null;
  }
  if (Number.isFinite(Number(body?.lat)) && Number.isFinite(Number(body?.lng))) {
    payload.lat = Number(body.lat);
    payload.lng = Number(body.lng);
  }
  if (Number.isFinite(Number(body?.local_x)) && Number.isFinite(Number(body?.local_y))) {
    payload.local_x = Number(body.local_x);
    payload.local_y = Number(body.local_y);
  }
  const keys = Object.keys(payload).filter((key) => key !== 'id');
  if (!keys.length) {
    return {
      ok: false,
      source: 'none',
      pin: null,
      message: 'Falta el campo a actualizar.',
      _httpStatus: null,
    };
  }
  try {
    const response = await patch('/updatecadprojectpin', { body: payload, signal });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        pin: response.data || payload,
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      pin: null,
      message: readCadApiError(response, 'No se pudo actualizar el pin.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: PATCH /updatecadprojectpin falló', error);
    return {
      ok: false,
      source: 'none',
      pin: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * DELETE /deletecadprojectpin?id=<uuid>.
 *
 * @param {{ id: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, source: 'api'|'none', data: object|null, message?: string, _httpStatus: number|null }>}
 */
export async function deleteCadProjectPin({ id } = {}, { signal } = {}) {
  const trimmed = String(id || '').trim();
  if (!trimmed) {
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'Falta el id del pin.',
      _httpStatus: null,
    };
  }
  try {
    const response = await del('/deletecadprojectpin', {
      params: { id: trimmed },
      signal,
    });
    if (response._httpOk) {
      return withStatus({
        ok: true,
        source: 'api',
        data: response.data || { id: trimmed },
      }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      data: null,
      message: readCadApiError(response, 'No se pudo eliminar el pin.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD showroom: DELETE /deletecadprojectpin falló', error);
    return {
      ok: false,
      source: 'none',
      data: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}


/**
 * Videos YouTube de parcela CAD (máx 4). Separado de fotos
 * (`custom_parcel_images`) y de UF / lot-content.
 * Envelope httpClient (`_httpOk` / `data`), igual que lot-content.
 */

/** @param {string} parcelId @param {string} youtubeUrl */
export const addCadParcelVideo = (parcelId, youtubeUrl) => post('/addcadparcelvideo', {
  body: { parcel_id: parcelId, youtube_url: youtubeUrl },
});

/** @param {string} parcelId @param {{ signal?: AbortSignal }} [options] */
export const getCadParcelVideos = (parcelId, { signal } = {}) => get('/getcadparcelvideos', {
  params: { parcel_id: parcelId },
  signal,
});

/** @param {string} videoId */
export const deleteCadParcelVideo = (videoId) => del('/deletecadparcelvideo', {
  params: { video_id: videoId },
});

/**
 * Adjuntos imagen/PDF de parcela CAD (máx 2). S3 bajo
 * `cad-images/projects/.../parcels/.../attachments/`. Separado de fotos.
 */

/**
 * GET /getcadparcelattachmentpresignedurl — ContentType + ContentLength.
 * @param {string} parcelId
 * @param {{ contentType: string, contentLength: number, fileName?: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 */
export const getCadParcelAttachmentPresignedUrl = (
  parcelId,
  { contentType, contentLength, fileName, file_name: fileNameSnake } = {},
  { signal } = {},
) => {
  const normalizedName = normalizeUserLotAttachmentFileName(fileName ?? fileNameSnake);
  return get('/getcadparcelattachmentpresignedurl', {
    params: {
      parcel_id: parcelId,
      contentType,
      contentLength,
      ...(normalizedName ? { file_name: normalizedName } : {}),
    },
    signal,
  });
};

/**
 * POST /savecadparcelattachment — cita s3_key + content_type tras PUT a S3.
 * @param {string} parcelId
 * @param {{ s3Key: string, contentType: string, fileName?: string }} params
 */
export const saveCadParcelAttachment = (
  parcelId,
  { s3Key, contentType, fileName, file_name: fileNameSnake, original_filename: originalFilename } = {},
) => post('/savecadparcelattachment', {
  body: {
    parcel_id: parcelId,
    s3_key: s3Key,
    content_type: contentType,
    file_name: normalizeUserLotAttachmentFileName(fileName ?? fileNameSnake ?? originalFilename),
  },
});

/** @param {string} parcelId @param {{ signal?: AbortSignal }} [options] */
export const getCadParcelAttachments = (parcelId, { signal } = {}) => get('/getcadparcelattachments', {
  params: { parcel_id: parcelId },
  signal,
});

/** @param {string} attachmentId */
export const deleteCadParcelAttachment = (attachmentId) => del('/deletecadparcelattachment', {
  params: { attachment_id: attachmentId },
});

/**
 * Presign → PUT S3 → save. Devuelve el adjunto (`data` del save).
 * @param {string} parcelId
 * @param {File|Blob} file
 * @param {{ signal?: AbortSignal, validate?: Function }} [options]
 */
export async function uploadCadParcelAttachment(parcelId, file, { signal, validate } = {}) {
  const validation = typeof validate === 'function'
    ? validate(file)
    : { ok: Boolean(file?.type && file?.size), contentType: file?.type, message: 'Archivo inválido.' };
  if (!validation.ok) {
    throw new Error(validation.message || 'Archivo inválido.');
  }

  const contentType = validation.contentType || file.type;
  const contentLength = Number(file.size);
  const fileName = normalizeUserLotAttachmentFileName(file?.name);

  const presigned = await getCadParcelAttachmentPresignedUrl(
    parcelId,
    { contentType, contentLength, fileName },
    { signal },
  );

  if (!presigned._httpOk || !presigned.data?.presignedUrl || !presigned.data?.s3Key) {
    const err = new Error(
      presigned?.message || presigned?.error || 'No se pudo preparar la subida del adjunto.',
    );
    err.apiResponse = presigned;
    throw err;
  }

  const {
    presignedUrl,
    s3Key,
    contentType: signedContentType,
  } = presigned.data;

  const putResult = await putUserLotAttachmentToS3(
    {
      presignedUrl,
      file,
      contentType: signedContentType || contentType,
    },
    { signal },
  );

  if (!putResult.ok) {
    throw new Error(putResult.message || 'No se pudo subir el archivo.');
  }

  const saveRes = await saveCadParcelAttachment(parcelId, {
    s3Key,
    contentType: signedContentType || contentType,
    fileName: fileName || file?.name,
  });

  if (!saveRes._httpOk || !saveRes.data) {
    const err = new Error(
      saveRes?.message || saveRes?.error || 'No se pudo guardar el adjunto.',
    );
    err.apiResponse = saveRes;
    throw err;
  }

  return saveRes;
}
